// ui/pages/ContractReviewsPage.tsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ApprovalRequest } from "../types/legalOps";
import {
  listApprovalRequestsByModule,
  deleteApprovalRequest,
} from "../services/approvalRequestsService";
import { updateStageDecision } from "../services/approvalFlowService";
import { db, firebase } from "../../firebaseClient";
import { useToast } from "../components/Toast";
import { useAiContext } from "../ai/AiContext";
import { LegalListModule } from "../modules/legal/LegalListModule";
import { contractModuleConfig } from "../modules/legal/config/ContractModuleConfig";
import TemplatesPage from "./TemplatesPage";
import ContractReviewForm from "../modules/legal/forms/ContractReviewForm";
import {
  canDeleteContractReviewHistory,
  canInitiateLegalRequest,
  isLegal,
  isManager,
  isSuperAdmin,
  isSuperUser,
} from "../config/roles";

// ✅ 獨立「簽核流程」彈窗用
import { Modal } from "../components/Modal";
import { ApprovalFlowPanel } from "../components/approval/ApprovalFlowPanel";
import { useApprovalFlow } from "../hooks/useApprovalFlow";

const HISTORY_STATUSES = [
  "APPROVED",
  "FULLY_APPROVED",
  "REJECTED",
  "RETURNED",
  "LEGAL_RETURNED",
  "PUBLISHED",
  "COMPLETED",
  "CANCELLED",
];

const getStatusLabel = (status: string) => {
  switch (status) {
    case "SUBMITTED":
      return "已送件";
    case "IN_REVIEW":
      return "審查中";
    case "WAITING_APPROVAL":
      return "主管核決中";
    case "APPROVED":
      return "已核准";
    case "FULLY_APPROVED":
      return "流程完成";
    case "REJECTED":
      return "已駁回";
    case "RETURNED":
      return "退回";
    case "LEGAL_RETURNED":
      return "法務退回";
    case "PUBLISHED":
      return "已公告";
    case "PENDING_REVIEW":
      return "簽核中";
    case "DRAFT":
      return "草稿";
    case "PENDING_MANAGER":
      return "待主管核決";
    case "PENDING_LEGAL":
      return "待法務審查";
    case "CANCELLED":
      return "已作廢";
    default:
      return status || "未知狀態";
  }
};

const getStatusColor = (status: string) => {
  if (status === "FULLY_APPROVED" || status === "APPROVED" || status === "PUBLISHED")
    return "var(--ok)";
  if (
    status === "REJECTED" ||
    status === "RETURNED" ||
    status === "LEGAL_RETURNED" ||
    status === "CANCELLED"
  )
    return "var(--danger)";
  if (status === "WAITING_APPROVAL" || status === "PENDING_MANAGER") return "#8b5cf6";
  if (status === "SUBMITTED" || status === "IN_REVIEW" || status === "PENDING_LEGAL")
    return "#fbbf24";
  return "#666";
};

export function ContractReviewsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"review" | "retention" | "templates">("review");

  const canSeeRetention = isLegal(user?.email) || isManager(user?.email) || isSuperAdmin(user?.email);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="row" style={{ borderBottom: "1px solid #333", marginBottom: 16 }}>
        <button
          className="navbtn"
          style={{
            width: "auto",
            borderBottom: activeTab === "review" ? "2px solid #fff" : "none",
            borderRadius: 0,
            color: activeTab === "review" ? "#fff" : "var(--muted)",
          }}
          onClick={() => setActiveTab("review")}
        >
          合約審查 (送件與簽核)
        </button>

        {canSeeRetention && (
          <button
            className="navbtn"
            style={{
              width: "auto",
              borderBottom: activeTab === "retention" ? "2px solid #fff" : "none",
              borderRadius: 0,
              color: activeTab === "retention" ? "#fff" : "var(--muted)",
            }}
            onClick={() => setActiveTab("retention")}
          >
            合約留存管理 (資料庫)
          </button>
        )}

        <button
          className="navbtn"
          style={{
            width: "auto",
            borderBottom: activeTab === "templates" ? "2px solid #fff" : "none",
            borderRadius: 0,
            color: activeTab === "templates" ? "#fff" : "var(--muted)",
          }}
          onClick={() => setActiveTab("templates")}
        >
          制式合約範本庫
        </button>
      </div>

      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {activeTab === "review" && <TabReviewSystem />}

        {activeTab === "retention" && canSeeRetention && (
          <LegalListModule config={{ ...contractModuleConfig, canEdit: true }} />
        )}

        {activeTab === "templates" && <TemplatesPage />}
      </div>
    </div>
  );
}

/**
 * ✅ 獨立「簽核流程」查看彈窗（ApprovalFlowPanel）
 */
function ApprovalFlowViewModal({
  request,
  onClose,
}: {
  request: ApprovalRequest;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const flowModule = "CONTRACT_REVIEW";

  const flowDocId =
    (request as any).approvalFlowId ||
    (request as any).flowId ||
    (request as any).approvalFlowDocId ||
    undefined;

  const { flow, loading } = useApprovalFlow(flowModule as any, request.id, flowDocId);

  const handleDecision = async (
    stageKey: any,
    decision: any,
    comment?: string,
    deptCode?: string,
    isDirectSign?: boolean
  ) => {
    try {
      if (!user) throw new Error("尚未登入。");
      if (!flow) throw new Error("尚未建立簽核流程（可能尚未送出呈核）。");

      const actorUid =
        (user as any)?.id || (user as any)?.uid || (user as any)?.userId || "";
      const actorName =
        (user as any)?.displayName ||
        (user as any)?.name ||
        (user as any)?.email ||
        "User";

      await updateStageDecision({
        flowId: (flow as any).id,
        stageKey,
        decision,
        comment: comment || "",
        actorUid,
        actorName,
        actorEmail: (user as any)?.email || undefined,
        deptCode,
        isDirectSign,
      });

      // ✅ 合約審理：把「駁回/退回原因」與快照寫回 approval_requests，供「檢視」右側顯示
      if ((decision === "REJECTED" || decision === "RETURNED") && (comment || "").trim()) {
        const reqRef = (db as any).collection("approval_requests").doc(request.id);
        const snap = await reqRef.get();
        const d = snap?.exists ? (snap.data() || {}) : {};

        const snapshot = {
          docCode: d.docCode,
          title: d.title,
          counterparty: d.counterparty,
          department: d.department,
          handlerName: d.handlerName,
          contractType: d.contractType,
          priority: d.priority,
          contractCurrency: d.contractCurrency,
          contractAmount: d.contractAmount,
          description: d.description,
          attachments: Array.isArray(d.attachments)
            ? d.attachments.map((f: any) => ({ name: f?.name, url: f?.url }))
            : [],
        };

        const nowStr = new Date().toISOString();
        await reqRef.update({
          lastRejectedAt: nowStr,
          lastRejectReason: comment || "",
          rejectHistory: (firebase as any).firestore.FieldValue.arrayUnion({
            decidedAt: nowStr,
            decidedBy: actorName,
            decidedEmail: (user as any)?.email || "",
            stageKey,
            decision,
            reason: comment || "",
            snapshot,
          }),
        });
      }
    } catch (e: any) {
      showToast(e?.message || "簽核失敗", "error");
      throw e;
    }
  };

  return (
    <Modal
      title={`簽核流程：${request.docCode || request.id.slice(0, 8)}｜${request.title}`}
      onClose={onClose}
      width={900}
    >
      <div style={{ padding: 12 }}>
        {loading ? (
          <div className="p" style={{ padding: 12, color: "var(--muted)" }}>
            載入簽核流程中…
          </div>
        ) : (
          <ApprovalFlowPanel
            flow={(flow ?? null) as any}
            mode="action"
            onDecision={handleDecision as any}
          />
        )}
      </div>
    </Modal>
  );
}


function TabReviewSystem() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { requestDelete } = useAiContext();

  const [items, setItems] = useState<ApprovalRequest[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // ✅ 這裡統一拿來「新建 / 檢視（帶入既有資料）」
  const [editingItem, setEditingItem] = useState<ApprovalRequest | null>(null);

  // ✅ 檢視時預設唯讀（若 ContractReviewForm 沒吃這個 prop 也不會壞，只是先把意圖傳進去）
  const [formReadOnly, setFormReadOnly] = useState(false);

  const [subTab, setSubTab] = useState<"ACTIVE" | "HISTORY">("ACTIVE");

  // ✅ 獨立簽核流程彈窗的選取項
  const [flowViewItem, setFlowViewItem] = useState<ApprovalRequest | null>(null);

  useEffect(() => {
    if (!user) return;
    const unsub = listApprovalRequestsByModule("contractDraft", (all) => {
      setItems(all);
    });
    return () => {
      unsub.then((fn) => fn());
    };
  }, [user]);

  const canDelete = (email?: string | null) => {
    return isSuperUser(email) || isSuperAdmin(email);
  };

  // ✅ 僅合約審查「歷史紀錄」tab 放寬刪除：只限 kc / ad
  const canDeleteHistory = canDeleteContractReviewHistory(user?.email);

  const handleDelete = (req: ApprovalRequest) => {
    requestDelete({
      targetType: "contractReview",
      targetId: req.id,
      summary: req.title,
      onConfirm: async () => {
        try {
          await deleteApprovalRequest(req.id);
          showToast("已刪除（或標記為刪除）");
        } catch (e: any) {
          showToast(e.message || "刪除失敗", "error");
        }
      },
    });
  };


  const canRequesterEditAfterReject = (item: ApprovalRequest) => {
    const status = (item as any)?.status;
    if (status !== "REJECTED" && status !== "RETURNED") return false;

    const userEmail = (user?.email || "").toLowerCase();
    const userName = (user?.displayName || "").trim();

    const emailCandidates = [
      (item as any).requesterEmail,
      (item as any).createdByEmail,
      (item as any).email,
      (item as any).createdBy,
    ]
      .filter(Boolean)
      .map((v: any) => String(v).toLowerCase());

    if (userEmail && emailCandidates.includes(userEmail)) return true;

    const nameCandidates = [
      (item as any).handlerName,
      (item as any).requesterName,
      (item as any).createdByName,
      (item as any).createdByDisplayName,
    ]
      .filter(Boolean)
      .map((v: any) => String(v).trim());

    if (userName && nameCandidates.includes(userName)) return true;

    return false;
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormReadOnly(false);
    setIsFormOpen(true);
  };

  const openView = (item: ApprovalRequest) => {
    setEditingItem(item);
    setFormReadOnly(!canRequesterEditAfterReject(item)); // 駁回後：原起單人可直接修正；其餘仍維持檢視唯讀
    setIsFormOpen(true);
  };

  /**
   * Unified logic for counts and lists
   */
  const activeList = items.filter((i) => {
    if (i.isDeleted) return false;
    if ((i as any).historyPolicyVersion === 2) {
      return (
        !(i as any).completedAt &&
        !["APPROVED", "FULLY_APPROVED", "COMPLETED", "PUBLISHED"].includes(i.status)
      );
    }
    return !HISTORY_STATUSES.includes(i.status);
  });

  const historyList = items.filter((i) => {
    if (i.isDeleted) return true;
    if ((i as any).historyPolicyVersion === 2) {
      return (
        !!(i as any).completedAt ||
        ["APPROVED", "FULLY_APPROVED", "COMPLETED", "PUBLISHED"].includes(i.status)
      );
    }
    return HISTORY_STATUSES.includes(i.status);
  });

  const displayList = subTab === "ACTIVE" ? activeList : historyList;

  // ✅ 用 any 避免 TS 因為多帶 prop 而報錯（最小變更）
  const ContractReviewFormAny = ContractReviewForm as any;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="row" style={{ gap: 0 }}>
          <button
            className={`navbtn ${subTab === "ACTIVE" ? "active" : ""}`}
            onClick={() => setSubTab("ACTIVE")}
          >
            進行中 ({activeList.length})
          </button>
          <button
            className={`navbtn ${subTab === "HISTORY" ? "active" : ""}`}
            onClick={() => setSubTab("HISTORY")}
          >
            歷史紀錄 ({historyList.length})
          </button>
        </div>

        {canInitiateLegalRequest(user?.role || "viewer") && (
          <button className="btn" style={{ width: "auto" }} onClick={openCreate}>
            + 提出合約審查
          </button>
        )}
      </div>

      <div
        style={{
          flex: 1,
          overflow: "auto",
          background: "#161616",
          border: "1px solid var(--border)",
          borderRadius: 12,
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#222", color: "#aaa", position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ padding: 12 }}>單號</th>
              <th style={{ padding: 12 }}>標題</th>
              <th style={{ padding: 12 }}>部門</th>
              <th style={{ padding: 12 }}>狀態</th>
              <th style={{ padding: 12, textAlign: "right" }}>操作</th>
            </tr>
          </thead>

          <tbody>
            {displayList.map((item) => (
              <tr
                key={item.id}
                style={{ borderBottom: "1px solid #333", opacity: item.isDeleted ? 0.6 : 1 }}
              >
                <td style={{ padding: 12, color: "#a5b4fc" }}>{item.docCode || item.id.slice(0, 8)}</td>

                <td style={{ padding: 12, fontWeight: 500 }}>
                  {item.isDeleted && (
                    <span style={{ color: "var(--danger)", marginRight: 4 }}>[管理刪除]</span>
                  )}
                  {item.title}
                </td>

                <td style={{ padding: 12 }}>
                  {item.department}{" "}
                  <span style={{ color: "#888" }}>
                    {(item as any).handlerName || (item as any).requesterName}
                  </span>
                </td>

                <td style={{ padding: 12 }}>
                  {item.isDeleted ? (
                    <span className="badge" style={{ color: "var(--danger)", borderColor: "var(--danger)" }}>
                      管理刪除
                    </span>
                  ) : (
                    <span
                      className="badge"
                      style={{ color: getStatusColor(item.status), borderColor: getStatusColor(item.status) }}
                    >
                      {getStatusLabel(item.status)}
                    </span>
                  )}
                </td>

                <td style={{ padding: 12, textAlign: "right" }}>
                  <button className="smallbtn" onClick={() => openView(item)}>
                    檢視
                  </button>

                  <button className="smallbtn" style={{ marginLeft: 8 }} onClick={() => setFlowViewItem(item)}>
                    簽核流程
                  </button>

                  {!item.isDeleted && ((subTab === "HISTORY" && canDeleteHistory) || (subTab !== "HISTORY" && canDelete(user?.email))) && (
                    <button
                      className="smallbtn"
                      style={{ marginLeft: 8, color: "var(--danger)", borderColor: "var(--danger)" }}
                      onClick={() => handleDelete(item)}
                    >
                      刪除
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {displayList.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>
                  無資料
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isFormOpen && (
        <ContractReviewFormAny
          initialData={editingItem}
          // 檢視預設唯讀（若表單有支援就會生效；沒有支援也不會影響既有行為）
          readOnly={formReadOnly}
          onClose={() => setIsFormOpen(false)}
          onSuccess={() => {
            setIsFormOpen(false);
            showToast(editingItem ? "已更新" : "合約會審已送出");
          }}
          allExisting={items}
        />
      )}

      {flowViewItem && <ApprovalFlowViewModal request={flowViewItem} onClose={() => setFlowViewItem(null)} />}
    </div>
  );
}
